import { NgComponentOutlet } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { tap } from 'rxjs';
import { environment } from '../environments/environment';
import { IAccount } from '../lib/auth/account.model';
import { AuthService } from '../lib/auth/auth.service';
import { AdminRoleService } from '../lib/services/admin-role.service';
import { AppUpdateService } from '../lib/services/app-update.service';
import { BrandService } from '../lib/services/brand.service';
import { LanguageService } from '../lib/services/language.service';
import { PermissionService } from '../lib/services/permission.service';
import { PlatformService } from '../lib/services/platform.service';
import { ToastService } from '../lib/services/toast.service';
import { LoadingComponent } from './components/shared/loading/loading.component';
import { LoadingService } from './components/shared/loading/loading.service';
import { DialogService } from './services/dialog.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, NgComponentOutlet, LoadingComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  protected readonly dialogService = inject(DialogService);

  private readonly authService = inject(AuthService);

  private readonly adminRoleService = inject(AdminRoleService);

  private readonly brandService = inject(BrandService);

  private readonly permissionService = inject(PermissionService);

  private readonly appUpdateService = inject(AppUpdateService);

  private readonly languageService = inject(LanguageService);

  private readonly toastService = inject(ToastService);

  private readonly platformService = inject(PlatformService);

  private readonly loadingService = inject(LoadingService);

  get isProd(): boolean {
    return environment.name === 'prod';
  }

  get isTest(): boolean {
    return !this.isProd && environment.name === 'test';
  }

  get isDev(): boolean {
    return !this.isProd && !this.isTest;
  }

  /**
   * 초기 라우트 활성화 여부
   * 초기 라우트 활성화 시 로딩 컴포넌트 비활성화
   */
  isInitialActivate = false;

  constructor() {
    // 로딩 컴포넌트 활성화
    this.loadingService.loadingOn();
  }

  ngOnInit(): void {
    if (this.platformService.isPlatformBrowser) {
      // 브라우저 언어 설정 기반으로 초기화
      this.languageService.init();

      this.updateLoginType();
    }

    this.authService.loginSubject$
      .pipe(
        tap((account) => {
          if (account) {
            this.initServices(account);
          }
        }),
      )
      .subscribe();

    // FIXME: 방식 변경
    this.appUpdateService.onUpdateAvailableMessage = (
      message: string,
      action: string,
    ) =>
      this.toastService.showToast(message, action, {
        containerClassNameList: ['p-3', 'top-0', 'end-0'],
      });
  }

  onActivate(): void {
    if (!this.isInitialActivate) {
      // 초기 라우트 활성화 여부 활성화
      this.isInitialActivate = true;
      // 로딩 컴포넌트 비활성화
      this.loadingService.loadingOff();
    }
  }

  /**
   * 서비스 초기 설정
   */
  private initServices(account: IAccount): void {
    this.adminRoleService.init(account);
    this.permissionService.initAsAdmin(account);
    this.languageService.init(account.brand);
    this.brandService.init(account.brand);
  }

  private updateLoginType(): void {
    const { host } = window.location;
    if (host.startsWith('partner.')) {
      this.authService.setLoginType('partner');
    } else if (host.startsWith('agent.')) {
      this.authService.setLoginType('agent');
    } else {
      this.authService.setLoginType('admin');
    }
  }
}
